import {Component, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {IsGranted} from '../../../../../../decorator/is-granted';
import {MetaTitle} from '../../../../../../decorator/meta-title';
import {Router, RouterLink} from '@angular/router';
import {FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {LoaderService} from '../../../../../../services/loader/loader.service';
import {SnackbarService} from '../../../../../../services/snackbar/snackbar.service';
import {AssetService} from '../../../../../../services/cms/asset.service';
import {AssetTypes} from '../../../../../../constants/asset-type';
import {UploadFile} from '../../../../../../models/upload-file';
import {UploadFileService} from '../../../../../../services/upload-file/upload-file.service';
import {ProductService} from '../../../../../../services/product/product.service';
import {Product} from '../../../../../../models/product';
import {LangService} from '../../../../../../services/language/lang.service';
import {Language} from '../../../../../../models/language';
import {forkJoin} from 'rxjs';

import {CodeEditor} from '@acrodata/code-editor';
import {Extension} from '@codemirror/state';
import {html} from '@codemirror/lang-html';
import {css} from '@codemirror/lang-css';
import {javascript} from '@codemirror/lang-javascript';
import {oneDark} from '@codemirror/theme-one-dark';

@Component({
  selector: 'app-content-library-add',
  imports: [
    RouterLink,
    FormsModule,
    ReactiveFormsModule,
    CodeEditor
],
  templateUrl: './content-library-add.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './content-library-add.component.scss'
})
@IsGranted(['ASSET_VIEW'])
@MetaTitle('Content Library Add')
export class ContentLibraryAddComponent implements OnInit {
  assetTypes = AssetTypes;
  uploadFile: UploadFile | null = null;
  editorContent: string = '';
  inputMode: 'upload' | 'editor' = 'upload';
  editorExtensions: Extension[] = [html(), oneDark];
  products: Product[] = [];
  languages: Language[] = [];

  form = new FormGroup({
    name: new FormControl('', Validators.required),
    type: new FormControl('', Validators.required),
    productId: new FormControl<number | null>(null, Validators.required),
    lang: new FormControl<string | null>(null),
  });

  constructor(
    private loader: LoaderService,
    private snackbar: SnackbarService,
    private router: Router,
    private assetService: AssetService,
    private uploadFileService: UploadFileService,
    private productService: ProductService,
    private langService: LangService,
  ) {
  }

  ngOnInit(): void {
    forkJoin([
      this.productService.findAll({sort: 'name'}),
      this.langService.getLanguages(),
    ]).subscribe(([products, languages]) => {
      this.products = products;
      this.languages = languages;
    });
  }

  isTextType(): boolean {
    return ['html', 'css', 'js'].includes(this.form.get('type')!.value ?? '');
  }

  onTypeChange(): void {
    this.uploadFile = null;
    this.editorContent = '';
    this.inputMode = 'upload';

    const type = this.form.get('type')!.value;
    if (type === 'css') {
      this.editorExtensions = [css(), oneDark];
    } else if (type === 'js') {
      this.editorExtensions = [javascript(), oneDark];
    } else {
      this.editorExtensions = [html(), oneDark];
    }
  }

  onSubmit(): void {
    if (!this.form.valid) {
      return;
    }

    const params: any = this.form.value;

    if (this.inputMode === 'editor' && this.editorContent) {
      const extension = this.form.get('type')!.value ?? 'html';
      params.document = {
        filename: params.name + '.' + extension,
        data: btoa(this.editorContent),
      };
    } else if (this.uploadFile) {
      params.document = this.uploadFile;
    }

    this.loader.startLoading();

    this.assetService.create(params).subscribe((result: any) => {
      this.loader.stopLoading();
      this.snackbar.success('Asset has been created.');
      this.router.navigateByUrl('/promotion-tools/cms/content-library/' + result.id).then();
    });
  }

  upload(event: Event): void {
    const target: any = event.target;
    const file = target?.files[0];

    this.uploadFileService.upload(file).subscribe((uploadFile: UploadFile) => {
      this.uploadFile = uploadFile;
    });
  }
}
